import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Contact, Plus, Trash2, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const downloadCsv = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = u; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Export fehlgeschlagen"); }
};
const empty = { name: "", contact_name: "", email: "", phone: "", address: "", notes: "" };

export default function Kunden() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { try { const r = await api.get("/customers"); setRows(r.data || []); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, contact_name: c.contact_name || "", email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name erforderlich");
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Gespeichert"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/customers/${id}`); toast.success("Kunde gelöscht"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader title="Kunden" subtitle="Kundenstammdaten mit automatischer Kundennummer. Marge je Kunde über alle Projekte." testid="kunden-header"
        action={<Button onClick={openNew} data-testid="customer-new" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Kunde</Button>} />
      {rows.length === 0 ? (
        <Card className="border-slate-200 rounded-xl"><EmptyState icon={Contact} title="Noch keine Kunden" subtitle="Lege deinen ersten Kunden an." testid="kunden-empty" /></Card>
      ) : (
        <div className="grid gap-4">
          {rows.map((c) => (
            <Card key={c.id} className="p-5 border-slate-200 rounded-xl" data-testid={`customer-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{c.customer_no}</span>
                    <span className="font-semibold text-slate-900">{c.name}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{[c.contact_name, c.email, c.phone].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="text-xs text-slate-400 mt-1">{c.project_count} Projekt(e)</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Marge gesamt</div>
                  <div className={`text-lg font-bold ${c.totals?.margin >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`customer-margin-${c.id}`}>{eur(c.totals?.margin)}</div>
                  <div className="text-xs text-slate-400">Umsatz {eur(c.totals?.revenue)} · Kosten {eur(c.totals?.cost)}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten</Button>
                <Button size="sm" variant="outline" onClick={() => downloadCsv(`/customers/${c.id}/export.csv`, `Kunde_${c.customer_no}.csv`)} data-testid={`customer-export-${c.id}`}><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => del(c.id)} data-testid={`customer-delete-${c.id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle>{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="customer-name" placeholder="Name / Firma *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="customer-contact" placeholder="Ansprechpartner" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="customer-email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="customer-phone" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <Input data-testid="customer-address" placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Input data-testid="customer-notes" placeholder="Notiz" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter><Button onClick={save} data-testid="customer-save" className="brand-bg text-white hover:opacity-90">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
